import React, { useEffect, useMemo, useState } from "react";
import { CModal, CModalBody, CModalHeader, CModalFooter, CFormRange, CFormSelect } from "@coreui/react";
import { Button, Spinner } from "react-bootstrap";
import paymentService from "@services/payment.service";
import { ProductDto } from "@dtos/product.dto";
import { t } from "i18next";

type Mode = "buy" | "add" | "renew";

type CreditsModalProps = {
    visible: boolean;
    onClose: () => void;
    mode: Mode;
    totalCredits?: number;
    maxCredits?: number;
    orderNumber?: string;
    currencies?: Array<String>;
    onSuccess?: () => void;
};

const formatEuro = (value: number, currency: string) =>
    (value / 100).toLocaleString("it-IT", { style: "currency", currency: currency });

const TITLES: Record<Mode, string> = {
    buy: "Preventivatore",
    add: "Preventivatore Aggiunta Crediti",
    renew: "Preventivatore Rinnovi",
};

export const ModalPricesAndPlans: React.FC<CreditsModalProps> = ({
    visible,
    onClose,
    mode,
    totalCredits = 0,
    maxCredits = 100,
    orderNumber,
    currencies,
    onSuccess,
}) => {
    const [accommodation, setAccommodation] = useState(1);
    const [loadingCalc, setLoadingCalc] = useState(false);
    const [loadingPay, setLoadingPay] = useState(false);
    const [price, setPrice] = useState<number | null>(null);
    const [success, setSuccess] = useState(false);
    const [errorAdd, setErrorAdd] = useState(false);
    const [product, setProduct] = useState<ProductDto | null>(null);
    const [currency, setCurrency] = useState("");
    const [renewalCredits, setRenewalCredits] = useState(totalCredits)
    useEffect(() => {
        if (visible) {
            setAccommodation(1);
            setPrice(null);
            setSuccess(false);
            setErrorAdd(false);
            setProduct(null);
            setLoadingCalc(false);
            setLoadingPay(false);
            setCurrency("");
            setRenewalCredits(totalCredits);
        }
    }, [visible, mode]);

    const maxRange = useMemo(() => {
        if (mode === "add") {
            // evita max <= 0
            return Math.max(1, maxCredits - (totalCredits ?? 0));
        }
        return maxCredits;
    }, [mode, maxCredits, totalCredits]);

    const isAddBlocked = mode === "add" && maxRange <= 1 && (totalCredits >= maxCredits);

    const choseCalculationEndpoint = async () => {
        let res
        switch (mode) {
            case "buy":
                res = await paymentService.getAccomodationPrice(accommodation, currency);
                return { obj: res.product, price: res.price }
            case "add":
                if (orderNumber == null) {
                    setErrorAdd(true)
                    return
                }
                res = await paymentService.getAddCreditsPrice(accommodation, orderNumber, currency);
                return { obj: res.product, price: res.price }
            case "renew":
                res = await paymentService.getAccomodationPrice(renewalCredits, currency);
                return { obj: res.product, price: res.price }
        }
    }

    const calculate = async () => {
        setLoadingCalc(true);
        setPrice(null);
        setSuccess(false);

        try {
            const res = await choseCalculationEndpoint();
            setPrice(res?.price);
            setProduct(res?.obj);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingCalc(false);
        }
    };

    const chosePaymentEndpoint = () => {
        if (!product) return;
        const obj = { id: product.id, product };

        switch (mode) {
            case "buy":
                return paymentService.getStripeUrl(obj, currency);
            case "add":
                if (orderNumber == null) {
                    setErrorAdd(true)
                    return
                }
                return paymentService.getStripeAddCreditsUrl(obj, orderNumber, currency);
            case "renew":
                if (orderNumber == null) {
                    setErrorAdd(true)
                    return
                }
                return paymentService.getStripeRenewalUrl(obj, orderNumber, currency);
        }
    }

    const pay = async () => {
        setLoadingPay(true);
        try {
            const res = await chosePaymentEndpoint();
            if (res.status === "SUCCESS" && res.sessionUrl) {
                window.open(res.sessionUrl, "_self");
                setSuccess(true);
                onSuccess?.();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingPay(false);
        }
    };

    return (
        <CModal visible={visible} onClose={onClose} alignment={"center"} >
            <CModalHeader style={{ backgroundColor: "var(--primary-color)", borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}>{TITLES[mode]}</CModalHeader>
            <CModalBody className="text-black" style={{ height: "300px", backgroundColor: "white" }}>
                {isAddBlocked ? (
                    <div className="text-warning fw-bold">
                        {t("text.max_credit_1")} ({maxCredits}). {t("text.max_credit_1")}
                    </div>
                ) : (
                    <>
                        <p>{t("text.estimator_text")}</p>
                        <label className="fw-bold my-1">{t("text.accomodation_number")} {mode === "renew" ? renewalCredits : accommodation}</label>
                        {mode === "renew" ?
                            <CFormRange
                                min={1}
                                max={maxRange}
                                value={renewalCredits}
                                onChange={(e) => {
                                    setPrice(null)
                                    setRenewalCredits(Number(e.target.value))
                                }}
                            /> :
                            <CFormRange
                                min={1}
                                max={maxRange}
                                value={accommodation}
                                onChange={(e) => {
                                    setPrice(null)
                                    setAccommodation(Number(e.target.value))
                                }}
                            />
                        }
                        <select
                            className="currency-select mt-2 "
                            value={currency}
                            onChange={(event) => {
                                setPrice(null)
                                setCurrency(event.target.value)
                            }}
                        >
                            <option value="" disabled>
                                {t("text.select_a_rate")}
                            </option>

                            {currencies?.map((el, i) => (
                                <option key={i} value={`${el}`}>
                                    {el}
                                </option>
                            ))}
                        </select>

                        {(price !== null && price !== 0 && !isNaN(price)) && (
                            <div className="mt-3">
                                {t("text.calculated_price")} <h4>{formatEuro(price, currency)}</h4>
                            </div>
                        )}

                        {errorAdd && (
                            <div className="mt-3 text-danger fw-bold">
                                {t("text.buy_pack_first")}
                            </div>
                        )}
                    </>
                )}
            </CModalBody>

            <CModalFooter style={{ 
                borderTop: "0px", 
                backgroundColor: "white", 
                borderBottomLeftRadius: '20px', 
                borderBottomRightRadius: '20px',
                display:"flex",
                justifyContent:"space-between"
                }}>
                <Button onClick={onClose}  style={{ backgroundColor: "white", border:"2px solid var(--primary-color)", color:"black"}}>
                    {t("button.close")}
                </Button>
                <div className="d-flex gap-2">
                    <Button variant="info" onClick={calculate} disabled={!currency || loadingCalc} style={{ color: "white" }}>
                        {loadingCalc ? <Spinner size="sm" /> : t("text.calculate")}
                    </Button>
                    <Button
                        variant="success"
                        disabled={isAddBlocked || price === null || loadingPay}
                        onClick={pay}
                    >
                        {loadingPay ? <Spinner size="sm" /> : t("text.pay")}
                    </Button>
                </div>
            </CModalFooter>
        </CModal>
    );
};